/**
 * Warehouse Shelf Map Report Runner
 *
 * Issue: #218 – CORE – Warehouse Shelf Map – Backend Report Runner + Template Registration
 * Issue: #369 – Shelf Map Report Missing Asset Detail (LIN/NSN/Nomenclature/QTY)
 * Issue: #370 – No Option To Select Multiple Locations For Report Generation
 *
 * Supports two generation modes triggered by report parameters:
 *   - map-id mode:           fetch all slots for a given shelf map layout
 *   - multi-location mode:   only include slots linked to selected location IDs
 *
 * Optional filters: aisleFilter (string), rowFilter (number)
 *
 * Fix #369: Inventory enrichment (LIN/NSN/Nomenclature/QTY) now runs for ALL
 * slots that have a linked location, not only when locationIds are explicitly
 * selected. The selectedLocationIds filter narrows which slots are shown but
 * does NOT gate whether inventory is fetched.
 */

import { API_CONFIG } from '@/config/api';
import { httpClient } from '@/utils/httpClient';
import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import {
  buildWarehouseShelfMapReportHtml,
  type ShelfMapReportSlot,
} from '@/reports/templates/warehouseShelfMapTemplate';
import type { ShelfMapLayoutWithSlots } from '@/services/shelfMapService';

export const runWarehouseShelfMapReport = async (
  params: Record<string, unknown>,
): Promise<void> => {
  const layoutId = (params.layoutId as string | undefined) ?? '';
  const aisleFilter = (params.aisleFilter as string | undefined) ?? '';
  const rowFilterRaw = params.rowFilter as string | number | undefined;
  const rowFilter = rowFilterRaw != null && rowFilterRaw !== '' ? Number(rowFilterRaw) : null;

  // Validate numeric filters — reject non-numeric input before any filtering runs (#524).
  // Passing NaN through would silently bypass the filter and return expanded result sets.
  if (rowFilter !== null && Number.isNaN(rowFilter)) {
    throw new Error('Shelf Row Number must be a valid number');
  }

  // Multi-location selection support (#370)
  const selectedLocationIds = (params.locationIds as string[] | undefined) ?? [];

  if (!layoutId) {
    throw new Error('layoutId is required for the shelf map report');
  }

  // Fetch layout + slots from the API
  const layoutData = await httpClient.get<ShelfMapLayoutWithSlots>(
    `${API_CONFIG.ENDPOINTS.SHELF_MAP}/${layoutId}`,
  );

  let slots = layoutData.slots ?? [];

  // Apply aisle filter
  if (aisleFilter) {
    const upperAisle = aisleFilter.trim().toUpperCase();
    slots = slots.filter((s) => s.aisle_label === upperAisle);
  }

  // Apply shelf/row filter
  if (rowFilter != null && !Number.isNaN(rowFilter)) {
    slots = slots.filter((s) => s.shelf_number === rowFilter);
  }

  // Apply multi-location selection filter: only show slots for selected locations
  if (selectedLocationIds.length > 0) {
    const idSet = new Set(selectedLocationIds);
    slots = slots.filter((s) => s.location_entity_id && idSet.has(s.location_entity_id));
  }

  const reportSlots: ShelfMapReportSlot[] = slots.map((s) => ({
    aisleLabel: s.aisle_label,
    shelfNumber: s.shelf_number,
    columnNumber: s.column_number,
    locationName: s.location_name,
    lin: '',
    nsn: '',
    nomenclature: s.location_name ?? s.label_override ?? '',
    quantity: 0,
    labelOverride: s.label_override,
  }));

  // Enrich ALL slots that have a linked location with inventory data (#369, #461).
  // Fix #461: when a location contains multiple items, expand into one report row
  // per item so no item is omitted from the report.
  const enrichedSlots: ShelfMapReportSlot[] = [];
  if (slots.length > 0) {
    for (const slot of reportSlots) {
      const srcSlot = slots.find(
        (s) =>
          s.aisle_label === slot.aisleLabel &&
          s.shelf_number === slot.shelfNumber &&
          s.column_number === slot.columnNumber,
      );
      if (!srcSlot?.location_entity_id) {
        enrichedSlots.push(slot);
        continue;
      }

      try {
        const inv = await httpClient.get<{
          location_name: string;
          items: Array<{
            lin: string;
            nsn: string;
            nomenclature: string;
            quantity: number;
          }>;
        }>(
          `${API_CONFIG.ENDPOINTS.SHELF_MAP}/${layoutId}/location/${srcSlot.location_entity_id}/inventory`,
        );
        if (inv.items.length === 0) {
          enrichedSlots.push(slot);
        } else {
          // One report row per inventory item so that all items in a shared
          // location are visible in the report (#461).
          for (const item of inv.items) {
            enrichedSlots.push({
              ...slot,
              lin: item.lin ?? '',
              nsn: item.nsn ?? '',
              nomenclature: item.nomenclature ?? slot.nomenclature,
              quantity: item.quantity,
            });
          }
        }
      } catch {
        // Non-fatal — keep the base slot row with empty fields
        enrichedSlots.push(slot);
      }
    }
  } else {
    enrichedSlots.push(...reportSlots);
  }

  const html = buildWarehouseShelfMapReportHtml({
    generatedAt: new Date(),
    layoutName: layoutData.name,
    warehouseName: layoutData.warehouse_name,
    aisleFilter: aisleFilter || null,
    rowFilter,
    selectedLocationIds,
    slots: enrichedSlots,
  });

  outputReport(
    html,
    `shelf-map-report-${layoutId.slice(0, 8)}.html`,
    getOutputFormat(params),
    getPreOpenedWindow(params),
  );
};
